import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Brain, Heart, Shield, Sparkles, Mic, Activity, Eye, Users, Globe, Zap, Smartphone } from 'lucide-react';
import therappiLogo from "@/assets/therappi-logo-cropped.png";
import therappiWhoopLogo from '@/assets/therappi-whoop-combined.svg';
import phoneCheckinMockup from '@/assets/phone-daily-tracking.png';
import amandaCharlesPhoto from '@/assets/amanda-charles-founder.jpeg';
import { Button } from '@/components/ui/button';

const ElevenLabsGrant = () => {
  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="bg-background">
      <div className="fixed top-4 right-4 z-50 print:hidden">
        <Button onClick={handlePrint} className="gap-2">
          <Sparkles className="h-4 w-4" />
          Download as PDF
        </Button>
      </div>

      <div className="max-w-5xl mx-auto px-6 pb-12 print:px-8 print:py-0">
        
        <div className="text-center pt-5">
          <div className="w-full max-w-3xl mx-auto mb-1">
            <img src={therappiLogo} alt="Therappi" className="w-full h-auto" />
          </div>
          <Badge className="text-base px-6 py-2 bg-gradient-to-r from-primary to-accent mb-2">
            ElevenLabs Grant Application 2025-2026
          </Badge>
          <p className="text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
            A Multidimensional Reflective AI Companion<br />
            <span className="text-lg">Where Psychology, Quantum Science and Voice Intelligence Meet</span>
          </p>
        </div>

        <div className="mb-12 mt-4 print:mb-10 print:mt-2">
          <Card className="p-10 print:shadow-none print:border-2 bg-gradient-to-br from-background via-primary/5 to-accent/5">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Brain className="h-7 w-7 text-primary" />
                  <h1 className="text-4xl font-bold">
                    What is THERAPPI<sup className="text-[0.6rem] align-super">®</sup>?
                  </h1>
                </div>
                <p className="text-lg leading-relaxed text-foreground/90 mb-6">
                  Therappi<sup className="text-[0.6rem] align-super">®</sup> is an AI-powered therapeutic companion designed to deliver personalised,
                  multidimensional support for mental and emotional wellbeing. Built at the intersection of 
                  psychology, quantum science and biometric data, it provides trauma-informed, highly tailored 
                  guidance that adapts to the user's patterns, physiology and lived experience.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Heart className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">Evidence-Based AI Reflection</h3>
                      <p className="text-sm text-muted-foreground">Integrates CBT, DBT, ACT, IFS and trauma-informed modalities</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Activity className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">WHOOP Biometric Integration</h3>
                      <p className="text-sm text-muted-foreground">Correlates HRV, recovery, sleep and strain with mental state</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Shield className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">QuantumSelf Psychology™</h3>
                      <p className="text-sm text-muted-foreground">Holistic framework spanning physical, emotional, mental, relational and spiritual dimensions</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Mic className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">Voice AI Integration</h3>
                      <p className="text-sm text-muted-foreground">Natural voice conversations with empathetic AI for deeper therapeutic connection</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Eye className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-base">Personalised Memory & Context</h3>
                      <p className="text-sm text-muted-foreground">Remembers your history to personalise your conversations and adapt support to your unique journey</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={phoneCheckinMockup}
                  alt="Therappi App Interface" 
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </Card>
        </div>

        <div className="mb-16 print:mb-10 print:pt-12">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold mb-4 flex items-center justify-center gap-3">
              <Mic className="h-8 w-8 text-primary" />
              <span>Why Voice Integration with ElevenLabs Transforms Therappi<sup className="text-[0.6rem] align-super">®</sup></span>
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
              Voice is the future of reflective AI. By integrating ElevenLabs' voice technology with Amanda Charles' 
              voice - an international award-winning chartered psychologist with 25+ years of professional practice experience - Therappi<sup className="text-[0.6rem] align-super">®</sup> becomes 
              the first AI reflection companion to offer truly human-like, clinically-grounded reflective conversations.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 print:shadow-none print:border-2 hover:shadow-xl transition-shadow">
              <Heart className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Human Connection</h3>
              <p className="text-sm text-muted-foreground">
                Amanda's voice creates warmth, empathy and trust - essential for therapeutic rapport. Users feel heard by a real psychologist, not a machine.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 hover:shadow-xl transition-shadow">
              <Shield className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Clinical Authority</h3>
              <p className="text-sm text-muted-foreground">
                The gravitas of an experienced psychologist's voice adds credibility and reassurance, particularly during moments of vulnerability or crisis.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 hover:shadow-xl transition-shadow">
              <Zap className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Accessibility Revolution</h3>
              <p className="text-sm text-muted-foreground">
                Voice makes therapy accessible to those with visual impairments, dyslexia, literacy challenges, or who simply prefer auditory learning and processing.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 hover:shadow-xl transition-shadow">
              <Activity className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Real-Time Support</h3>
              <p className="text-sm text-muted-foreground">
                Users can speak freely while walking, exercising, or during daily activities - receiving instant, hands-free therapeutic guidance exactly when needed.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 hover:shadow-xl transition-shadow">
              <Eye className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Emotional Nuance</h3>
              <p className="text-sm text-muted-foreground">
                Voice captures tone, pacing and emotional inflection - enabling Therappi<sup className="text-[0.6rem] align-super">®</sup> to respond with appropriate empathy, validation and clinical sensitivity.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 hover:shadow-xl transition-shadow">
              <Globe className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Global Reach</h3>
              <p className="text-sm text-muted-foreground">
                ElevenLabs' multilingual capabilities mean Amanda's therapeutic approach can reach non-English speakers worldwide, democratising access to evidence-based mental health support.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 hover:shadow-xl transition-shadow">
              <Users className="h-10 w-10 text-primary mb-4" />
              <h3 className="text-lg font-semibold mb-2">Privacy & Confidentiality</h3>
              <p className="text-sm text-muted-foreground">
                Voice interactions feel private and personal, reducing stigma and allowing users to engage with sensitive topics more comfortably than text-based systems.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 hover:shadow-xl transition-shadow">
              <Shield className="h-10 w-10 text-accent mb-4" />
              <h3 className="text-lg font-semibold mb-2">Safety & Ethical Standards</h3>
              <p className="text-sm text-muted-foreground">
                Built with trauma-informed principles, evidence-based protocols, and ethical safeguards ensuring responsible AI reflection that prioritises user wellbeing and clinical safety.
              </p>
            </Card>
          </div>
        </div>

        <div className="mb-16 print:mb-10 print:pt-12">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold mb-4 flex items-center justify-center gap-3">
              <Smartphone className="h-8 w-8 text-primary" />
              How THERAPPI<sup className="text-[0.35em] -ml-1.5" style={{ verticalAlign: 'super', position: 'relative', top: '-0.8em' }}>®</sup> Will Use ElevenLabs
            </h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              A two-phase implementation strategy to integrate voice into every aspect of the reflective experience
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <Card className="p-8 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold">1</span>
                </div>
                <h3 className="text-2xl font-bold">Phase 1: Voice-Enhanced AI Companion</h3>
              </div>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Conversational Therapy Sessions</h4>
                    <p className="text-muted-foreground">Users speak directly to the AI companion using Amanda's voice for warm, empathetic responses in real-time dialogue</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Voice-Activated Check-Ins</h4>
                    <p className="text-muted-foreground">Morning and evening check-ins via voice, allowing users to share mood, energy, and emotional state naturally while multitasking</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Crisis Voice Support</h4>
                    <p className="text-muted-foreground">Instant voice-based grounding and de-escalation when users are in distress - guiding breathwork, somatic techniques, and safety planning</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Guided Audio Exercises</h4>
                    <p className="text-muted-foreground">Meditation, breathwork, visualisations, and body scan exercises delivered in Amanda's voice for maximum therapeutic impact</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Personalised Affirmations</h4>
                    <p className="text-muted-foreground">Custom affirmations and therapeutic reminders delivered throughout the day based on user goals and emotional patterns</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-primary/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-primary rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Voice Journaling</h4>
                    <p className="text-muted-foreground">Users can voice-journal their thoughts and feelings, with AI providing reflections and wellbeing insights using Amanda's voice</p>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 print:shadow-none print:border-2 bg-gradient-to-br from-accent/5 to-primary/5">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-16 h-16 bg-accent text-accent-foreground rounded-full flex items-center justify-center">
                  <span className="text-2xl font-bold">2</span>
                </div>
                <h3 className="text-2xl font-bold">Phase 2: Advanced Voice Intelligence</h3>
              </div>
              <div className="space-y-4 text-sm">
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Emotional Tone Analysis</h4>
                    <p className="text-muted-foreground">Detect emotional states from voice patterns (stress, sadness, anxiety) to adjust therapeutic responses dynamically and provide early intervention</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Multilingual Therapy</h4>
                    <p className="text-muted-foreground">Leverage ElevenLabs' multilingual capabilities to make Amanda's therapeutic approach accessible globally in users' native languages</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Contextual Voice Responses</h4>
                    <p className="text-muted-foreground">Voice responses adapt based on time of day, recent emotional patterns, biometric data, and therapeutic goals for truly personalised care</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Voice-Biometric Correlation</h4>
                    <p className="text-muted-foreground">Cross-reference voice patterns with WHOOP data (HRV, recovery, sleep quality) to identify mind-body disconnects and recommend interventions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Therapeutic Voice Coaching</h4>
                    <p className="text-muted-foreground">Real-time voice guidance during activities like exercise, meditation, or difficult conversations to maintain emotional regulation</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="p-1.5 bg-accent/20 rounded-full mt-0.5">
                    <div className="w-2 h-2 bg-accent rounded-full"></div>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">Voice-Enabled Goal Tracking</h4>
                    <p className="text-muted-foreground">Users set and track therapeutic goals through voice, receiving spoken progress updates and encouragement to maintain motivation</p>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>

        <div className="mb-16 print:mb-10 print:pt-12">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold mb-4">Comprehensive Product Features</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              A complete therapeutic ecosystem addressing physical, emotional, mental, relational, and spiritual wellbeing
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="p-6 print:shadow-none print:border-2">
              <div className="flex items-center gap-3 mb-4">
                <Brain className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-bold">Therappi<sup className="text-[0.6rem] align-super">®</sup> AI Psychologist</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Conversational AI companion integrating CBT, DBT, ACT, IFS, and trauma-informed modalities. Provides personalised reflective conversations, crisis intervention, and evidence-based interventions.
              </p>
              <div className="text-xs text-primary font-semibold">ElevenLabs voice brings this to life</div>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2">
              <div className="flex items-center gap-3 mb-4">
                <Activity className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-bold">Intelligent Check-ins & Tracking</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Morning and evening check-ins capturing mood, energy, sleep quality, symptoms, and menstrual cycle tracking. Pattern recognition identifies triggers and predicts emotional states before crises occur.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2">
              <div className="flex items-center gap-3 mb-4">
                <Heart className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-bold">Evidence-Based Coping Tools</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Extensive library of grounding techniques, breathwork, meditation, somatic exercises, CBT/DBT worksheets, and crisis intervention tools. Personalised recommendations based on user patterns.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2">
              <div className="flex items-center gap-3 mb-4">
                <Zap className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-bold">Advanced Analytics & Insights</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                AI-powered pattern recognition across mood, sleep, nutrition, exercise, biometrics, and life events. Predictive analytics forecast emotional states and recommend preventative interventions.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2">
              <div className="flex items-center gap-3 mb-4">
                <Globe className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-bold">Holistic Life Tracking</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Track nutrition (food quality, inflammation, gut health), exercise, hydration, life events, appointments, and personal goals. Correlate physical inputs with mental/emotional outputs.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5">
              <div className="flex items-center gap-3 mb-4">
                <Mic className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-bold">Voice Mode (ElevenLabs)</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Conversational voice therapy using Amanda Charles' voice for warm, empathetic therapeutic dialogues. Hands-free check-ins, crisis support, guided meditations, and voice journaling.
              </p>
              <div className="text-xs text-primary font-bold">✨ Powered by ElevenLabs</div>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2">
              <div className="flex items-center gap-3 mb-4">
                <Users className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-bold">Community & Support</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Peer support forums, educational content, therapeutic courses, and access to human therapists for those needing additional support. Safe, moderated environment for connection.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2">
              <div className="flex items-center gap-3 mb-4">
                <Activity className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-bold">Inflammatory Load (IL) System</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Proprietary metric quantifying cumulative physical, emotional, and environmental stressors. Predicts burnout, illness susceptibility, and emotional dysregulation before symptoms appear.
              </p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles className="h-8 w-8 text-primary" />
                <h3 className="text-lg font-bold">Timeline Healing & Identity Work</h3>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                Document life events, traumas, and significant moments to create a healing narrative. AI identifies patterns across the lifespan and guides identity transformation work.
              </p>
            </Card>
          </div>
        </div>

        <div className="mb-16 print:mb-10 print:pt-12">
          <Card className="p-10 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="flex items-center gap-3 mb-6">
                  <Activity className="h-7 w-7 text-primary" />
                  <h2 className="text-3xl font-bold">WHOOP Integration</h2>
                </div>
                <p className="text-base leading-relaxed text-foreground/90 mb-6">
                  Therappi<sup className="text-[0.6rem] align-super">®</sup> is the first-of-its-kind AI reflection platform to integrate with WHOOP biometric data, 
                  creating a revolutionary mind-body connection dashboard. This integration correlates psychological 
                  states with physiological markers to provide unprecedented insights into mental health patterns.
                </p>
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Heart className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">1. HRV (Heart Rate Variability)</h3>
                      <p className="text-sm text-muted-foreground">Correlates with nervous system regulation and stress resilience. Therappi tracks HRV trends to identify anxiety patterns, predict emotional dysregulation, and recommend targeted breathwork and grounding exercises when HRV drops below baseline.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Activity className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">2. Recovery Score</h3>
                      <p className="text-sm text-muted-foreground">Measures physiological readiness for physical and mental demands. Low recovery scores trigger modified therapy approaches, suggesting rest-focused interventions and reduced cognitive load activities to prevent burnout.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Zap className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">3. Sleep Performance</h3>
                      <p className="text-sm text-muted-foreground">Tracks sleep stages, efficiency, and disturbances. Therappi correlates poor sleep with mood instability, recommends sleep hygiene interventions, and adjusts therapeutic content timing to align with natural circadian rhythms.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Zap className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">4. Strain</h3>
                      <p className="text-sm text-muted-foreground">Quantifies cardiovascular and physiological load from physical and mental exertion. Chronic high strain without adequate recovery signals overextension, prompting Therappi to suggest boundary-setting work and stress management techniques.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Activity className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold">5. Respiratory Rate</h3>
                      <p className="text-sm text-muted-foreground">Elevated resting respiratory rate indicates physiological stress or illness onset. Therappi monitors this to detect pre-symptomatic stress responses and guide preventative somatic regulation before emotional crisis occurs.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={therappiWhoopLogo} 
                  alt="Therappi + WHOOP Integration" 
                  className="w-full h-auto"
                />
              </div>
            </div>
          </Card>
        </div>

        <div className="mb-16 print:mb-10 page-break-before print:pt-12">
          <Card className="p-10 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 via-primary/5 to-accent/5">
            <div className="grid lg:grid-cols-3 gap-10 items-start">
              <div className="lg:col-span-1 flex flex-col items-center text-center">
                <img 
                  src={amandaCharlesPhoto} 
                  alt="Amanda Charles - Founder" 
                  className="w-80 h-80 rounded-2xl shadow-xl mb-6 object-cover"
                />
                <h3 className="text-2xl font-bold mb-2">Amanda Charles</h3>
                <p className="text-lg text-primary font-semibold mb-2">
                  CEO and Founder of Therappi<sup className="text-[0.6rem] align-super">®</sup>
                </p>
                <p className="text-sm text-muted-foreground">
                  The Psychic Psychologist<sup className="text-[0.6rem] align-super">®</sup>
                </p>
              </div>
              <div className="lg:col-span-2">
                <div className="flex items-center gap-3 mb-6">
                  <Sparkles className="h-7 w-7 text-primary" />
                  <h2 className="text-3xl font-bold">About the Founder</h2>
                </div>
                <div className="space-y-4 text-foreground/90 leading-relaxed">
                  <p className="text-base">
                    <strong>Amanda Charles, The Psychic Psychologist<sup className="text-[0.6rem] align-super">®</sup></strong>, 
                    is an international award-winning Chartered Counselling Psychologist registered with the British Psychological 
                    Society (BPS) and the Health and Care Professions Council (HCPC), bringing 25+ years of extensive reflective experience.
                  </p>
                  <p className="text-base">
                    She is the founder of <strong>QuantumSelf Psychology™</strong>, an entirely new paradigm that bridges 
                    science and spirituality by integrating research-based psychological interventions with shamanic energy 
                    medicine, quantum science, psycho-neuro-immunology, psycho-neuro-endocrinology, consciousness studies, 
                    quantum biology and epigenetics.
                  </p>
                  <p className="text-base">
                    Amanda is an <strong>International Speaker and Author</strong> of the multi-award-winning No.1 international 
                    bestseller <em>The Psychic Psychologist<sup className="text-[0.6rem] align-super">®</sup>: Heal Your Past, Find Peace in the Present, Transform Your Future</em>, 
                    published by Hay House. In 2025, she was awarded the <strong>Nautilus Silver Book Award</strong> in 
                    Psychology/Mental and Emotional Wellbeing - an honour previously given to King Charles III and the Dalai Lama - 
                    and named both <strong>Winner and Finalist</strong> across four categories at the International Book Awards.
                  </p>
                  <p className="text-base">
                    Her dedication to this mission is deeply personal. Amanda combined these disciplines to recover from the trauma 
                    of suddenly losing every member of her immediate family, navigating marriage breakdown, healing herself from 
                    chronic illnesses, pituitary tumours, infertility, and more recently, a suspected stroke. This lived experience 
                    fuels her commitment to demonstrating that we are capable of far more than we've been taught and can heal ourselves 
                    from suffering and transform our lives.
                  </p>
                  <p className="text-base">
                    As an international speaker, Amanda has delivered her teachings at the Flow Summit alongside <strong>Dr Joe Dispenza, 
                    Eckhart Tolle, Deepak Chopra and the Dalai Lama</strong>. She is a regular speaker on the main stage at the Mind Body 
                    Spirit Festival (London Olympia, NEC Birmingham), and provides professional training through The School of QuantumSelf 
                    Psychology™ and CPD-accredited workshops for PESI Inc. - the largest provider of continuing professional development 
                    for psychologists, psychotherapists and health professionals in the UK, Europe and internationally.
                  </p>
                  <p className="text-base">
                    Amanda's work has appeared in prestigious publications including <em>The Telegraph, The Guardian, The Daily Mail, 
                    Metro, Newsweek, The Mirror, Red, Glamour, Health & Wellbeing</em>, and <em>Kindred Spirit</em>, alongside numerous 
                    features on BBC Radio, UK Health Radio and spotlights on Hay House's <em>You Can Heal Your Life</em> and 
                    <em>Empower You</em> platforms.
                  </p>
                  <p className="text-base font-semibold text-primary">
                    Therappi<sup className="text-[0.6rem] align-super">®</sup> is the culmination of Amanda's life's work - bringing 
                    her 25+ years of clinical expertise, quantum science insights, trauma-informed care, and intuitive wisdom into an 
                    AI companion voiced by her as a psychologist. This scales therapeutic healing to millions worldwide, democratising 
                    access to world-class mental health support through voice-powered, evidence-based AI reflection.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>

        <div className="mb-16 print:mb-10 print:pt-12">
          <div className="text-center mb-10">
            <h2 className="text-4xl font-bold mb-4">Market Opportunity</h2>
            <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
              Mental health is a global crisis - and Therappi<sup className="text-[0.6rem] align-super">®</sup> is positioned to address it at scale
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <Card className="p-6 print:shadow-none print:border-2 text-center">
              <div className="text-4xl font-bold text-primary mb-2">$560B</div>
              <h3 className="font-semibold mb-2">Global Mental Health Market</h3>
              <p className="text-sm text-muted-foreground">Projected to reach $560 billion by 2030 (CAGR 3.5-4.6%)</p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 text-center">
              <div className="text-4xl font-bold text-primary mb-2">1B+</div>
              <h3 className="font-semibold mb-2">Global Mental Health Burden</h3>
              <p className="text-sm text-muted-foreground">Over 1 billion people living with mental health disorders worldwide (WHO, 2025)</p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 text-center">
              <div className="text-4xl font-bold text-primary mb-2">70%</div>
              <h3 className="font-semibold mb-2">Treatment Gap</h3>
              <p className="text-sm text-muted-foreground">70% of people with mental health conditions in low-middle income countries receive no treatment due to cost, stigma, and access barriers</p>
            </Card>

            <Card className="p-6 print:shadow-none print:border-2 text-center">
              <div className="text-4xl font-bold text-primary mb-2">$1T</div>
              <h3 className="font-semibold mb-2">Economic Impact</h3>
              <p className="text-sm text-muted-foreground">Depression and anxiety cost the global economy $1 trillion annually in lost productivity (WHO, 2024)</p>
            </Card>
          </div>
        </div>

        <div className="mb-16 print:mb-10">
          <Card className="p-10 print:shadow-none print:border-2 bg-gradient-to-br from-primary/5 to-accent/5 text-center">
            <h2 className="text-3xl font-bold mb-6">The Impact of Voice-Powered Therapy</h2>
            <p className="text-xl text-foreground/90 max-w-3xl mx-auto leading-relaxed mb-8">
              By integrating ElevenLabs' voice technology with Amanda Charles' 25+ years of clinical expertise, 
              Therappi<sup className="text-[0.6rem] align-super">®</sup> will democratise access to world-class reflective support for millions worldwide - 
              breaking down barriers of cost, geography, stigma, and accessibility while maintaining the warmth, 
              empathy, and clinical authority of human therapy.
            </p>
            <div className="grid md:grid-cols-3 gap-6 text-center">
              <div>
                <div className="text-4xl font-bold text-primary mb-2">Accessible</div>
                <p className="text-sm text-muted-foreground">24/7 support regardless of location, income, or physical ability</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">Human-Like</div>
                <p className="text-sm text-muted-foreground">Warm, empathetic voice creating genuine therapeutic connection</p>
              </div>
              <div>
                <div className="text-4xl font-bold text-primary mb-2">Evidence-Based</div>
                <p className="text-sm text-muted-foreground">Clinically-grounded interventions from an award-winning psychologist</p>
              </div>
            </div>
          </Card>
        </div>

        <div className="text-center pt-8 border-t border-border print:pt-6">
          <p className="text-sm text-muted-foreground">
            Thank you for considering Therappi<sup className="text-[0.6rem] align-super">®</sup> for the ElevenLabs Grant. We're excited to partner with you in revolutionising mental health care.
          </p>
          <p className="text-sm text-muted-foreground mt-2">
            Contact: <span className="text-primary font-semibold">support@therappi.app</span>
          </p>
          <a 
            href="https://www.therappi.app" 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-primary hover:underline font-semibold inline-block mt-1"
          >
            www.therappi.app
          </a>
        </div>
      </div>
    </div>
  );
};

export default ElevenLabsGrant;